import { getComputedColorValue } from '@/helpers/Whitelabels/colorPicker';
import { Box, InputLabel, TextField } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

interface IProps {
  title: string;
  name: string;
  sx?: SxProps<Theme>;
  className?: string;
  placeholder?: string;
}

const ColorInput = forwardRef<HTMLDivElement, IProps>(
  ({ title, name, sx, className, placeholder }: IProps, outerRef) => {
    const { control } = useFormContext();
    const innerRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(outerRef, () => innerRef.current!, []);
    const controlId = useMemo(() => `${name}_control`, [name]);

    return (
      <Box sx={sx} className={className}>
        <Controller
          control={control}
          name={name}
          rules={{
            required: {
              value: true,
              message: `${title} is required`,
            },
            validate: () => {
              const errorMessage = `${title} must be valid`;
              const background = getComputedColorValue(innerRef);
              return !!background || errorMessage;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <>
              <InputLabel htmlFor={controlId} className='font-medium text-sm'>
                {title}
              </InputLabel>
              <TextField
                {...field}
                variant='outlined'
                id={controlId}
                error={!!error}
                helperText={error?.message}
                slotProps={{
                  input: {
                    startAdornment: (
                      <Box
                        ref={innerRef}
                        sx={{
                          background: field.value,
                          border: '1px dotted',
                          width: '22px',
                          height: '22px',
                          borderRadius: '4px',
                          flexShrink: 0,
                        }}
                      />
                    ),
                    placeholder,
                  },
                }}
              />
            </>
          )}
        />
      </Box>
    );
  },
);

export default ColorInput;
